import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import axiosInstance from '../utils/axiosInstance';
import Calendar from '../components/Calendar';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Avatar,
  ListItemIcon,
  Paper,
  Popper,
  Grow,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Radio,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Badge,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Class as InstructorIcon,
  KeyboardArrowDown,
  Mail as MailIcon,
  CalendarToday as CalendarIcon,
  ViewSidebar as ViewSidebarIcon,
  ViewSidebarOutlined as ViewSidebarOutlinedIcon,
  Notes as NotesIcon,
  SettingsOutlined as SettingsOutlinedIcon
} from '@mui/icons-material';
import { logout, setUnreadCount } from '../slices/authSlice';
import { 
  toggleLeftSidebar, 
  toggleRightSidebar, 
  toggleBottomPanel 
} from '../slices/uiSlice';

const formatRole = (role) => {
  switch (role?.toLowerCase()) {
    case 'org_admin':
      return 'Admin';
    case 'systemadmin':
      return 'System Admin';
    case 'admin':
      return 'Administrator';
    case 'instructor':
      return 'Instructor';
    case 'learner':
      return 'Learner';
    default:
      return 'User';
  }
};

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isCourseView = location.pathname.includes('/courses/');
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const [selectedRole, setSelectedRole] = useState('learner');

  const { token, user, unreadCount: storeUnreadCount } = useSelector((state) => {
    console.log('Auth State:', state.auth);
    return state.auth;
  });

  const { showBottomPanel, showLeftSidebar, showRightSidebar } = useSelector((state) => state.ui);

  const isLoggedIn = Boolean(token && user);
  const isAdmin = user?.role === 'admin';
  const isInstructor = user?.role === 'instructor';
  const isOrgAdmin = user?.role === 'org_admin';
  const isSuperAdmin = user?.role === 'superadmin';

  const canShowSidebarControls = isAdmin || isInstructor || isOrgAdmin || isSuperAdmin;

  // Enhanced function to get formatted avatar URL
  const getFormattedAvatarUrl = (avatarUrl) => {
    if (!avatarUrl) return '';
    
    // For debugging
    console.log('Avatar URL in Header:', avatarUrl);
    
    // If it's already an absolute URL, return it
    if (avatarUrl.startsWith('http')) {
      return avatarUrl;
    }
    
    // If it's a relative path, prepend the API base URL
    let baseUrl = process.env.REACT_APP_API_URL || '';
    
    // Remove trailing slash from baseUrl if it exists and avatarUrl starts with slash
    if (baseUrl.endsWith('/') && avatarUrl.startsWith('/')) {
      baseUrl = baseUrl.slice(0, -1);
    }
    
    // Add slash if needed
    if (!baseUrl.endsWith('/') && !avatarUrl.startsWith('/')) {
      return `${baseUrl}/${avatarUrl}`;
    }
    
    const fullUrl = `${baseUrl}${avatarUrl}`;
    console.log('Full avatar URL:', fullUrl);
    return fullUrl;
  };

  const [unreadCount, setLocalUnreadCount] = useState(0);
  useEffect(() => {
    if (user) {
      dispatch(setUnreadCount());

      const interval = setInterval(() => {
        dispatch(setUnreadCount());
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (storeUnreadCount !== undefined) {
      setLocalUnreadCount(storeUnreadCount);
    }
  }, [storeUnreadCount]);

  useEffect(() => {
    if (isLoggedIn) {
      if (user?.role === 'admin') {
        setSelectedRole('administrator');
        if (location.pathname === '/login' || location.pathname === '/') {
          navigate('/admin/reports');
        }
      } else if (user?.role === 'instructor') {
        setSelectedRole('instructor');
        if (location.pathname === '/login' || location.pathname === '/') {
          navigate('/admin/reports');
        }
      }
    }
  }, [isLoggedIn, user?.role, navigate, location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/login' && location.pathname !== '/') {
      if (location.pathname === '/dashboard') {
        setSelectedRole('learner');
      } else if (location.pathname.startsWith('/admin')) {
        setSelectedRole('administrator');
      } else if (location.pathname.startsWith('/instructor')) {
        setSelectedRole('instructor');
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isLoggedIn && isAdmin) {
      if (window.location.pathname === '/login') {
        navigate('/admin/reports');
      }
    }
  }, [isLoggedIn, isAdmin, navigate]);

  // Add useEffect to log user avatar changes for debugging
  useEffect(() => {
    if (user) {
      console.log('User state in Header updated:', {
        id: user.id,
        name: `${user.first_name || ''} ${user.last_name || ''}`,
        avatar_url: user.avatar_url,
        formatted_url: user.avatar_url ? getFormattedAvatarUrl(user.avatar_url) : null
      });
    }
  }, [user]);

  const getDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.first_name) {
      return user.first_name;
    }
    if (user?.username) {
      return user.username;
    }
    return '';
  };

  const displayName = getDisplayName();

  useEffect(() => {
    console.log('User Data in Header:', {
      first_name: user?.first_name,
      last_name: user?.last_name,
      username: user?.username,
      avatar_url: user?.avatar_url,
      displayName
    });
  }, [user, displayName]);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    setOpen(false);
    navigate('/login');
  };

  const handleRoleChange = (event) => {
    const newRole = event.target.value;
    setSelectedRole(newRole);
    setOpen(false);

    switch (newRole) {
      case 'administrator':
        navigate('/admin/reports');
        break;
      case 'instructor':
        navigate('/admin/reports');
        break;
      case 'learner':
        navigate('/dashboard');
        break;
      default:
        break;
    }
  };

  const handleMouseEnter = () => {
    setOpen(true);
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setOpen(false);
  };

  const handleMessagesClick = () => {
    navigate('/messages');
  };

  const roleMenu = (
    <Box
      ref={anchorRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          cursor: 'pointer',
          padding: '8px',
          borderRadius: 1,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <Avatar
          alt={displayName}
          src={getFormattedAvatarUrl(user?.avatar_url)}
          sx={{
            width: 40,
            height: 40,
            bgcolor: 'primary.main',
            border: user?.avatar_url ? '2px solid #fff' : 'none',
          }}
          // Add a key prop to force re-render when avatar_url changes
          key={user?.avatar_url || 'default-avatar'}
        >
          {/* Only show initials when no avatar URL is available */}
          {!user?.avatar_url && (user?.first_name?.[0] || user?.username?.[0])}
        </Avatar>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0
        }}>
          <Typography
            variant="subtitle1"
            sx={{
              lineHeight: 1.2,
              fontWeight: 700
            }}
          >
            {displayName}
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{
              textTransform: 'capitalize',
              lineHeight: 1.2
            }}
          >
            {formatRole(user?.role)}
          </Typography>
        </Box>
        <KeyboardArrowDown
          sx={{
            ml: 1,
            color: 'text.secondary'
          }}
        />
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        placement="bottom-end"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              sx={{
                mt: 1,
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                minWidth: '220px',
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  <MenuItem onClick={handleProfileClick}>
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="body2">Profile Settings</Typography>
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="body2">Logout</Typography>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: '#ffffff',
        color: '#333333',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
      elevation={2}
    >
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: { xs: '56px', sm: '64px' },
          paddingX: 0,
          padding: 0,
          margin: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img
            src="/images/santech_lms-logo.png"
            alt="SANTECH LMS Logo"
            style={{
              width: '118px',
              height: '40px',
              marginRight: '8px',
            }}
          />
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              display: { xs: 'none', sm: 'block' },
              cursor: 'pointer',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 500,
            }}
          >
            Learning Management
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isLoggedIn && (
            <>
              {/* Message Icon */}
              <Tooltip title="Messages">
                <IconButton
                  color="inherit"
                  onClick={handleMessagesClick}
                  sx={{ mr: 1 }}
                >
                  <Badge 
                    badgeContent={unreadCount} 
                    color="error" 
                    invisible={!unreadCount}
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -3,
                        top: 3,
                        minWidth: 16,
                        height: 16,
                        fontSize: '0.65rem'
                      }
                    }}
                  >
                    <MailIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              
              {canShowSidebarControls && isCourseView && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  mx: 2,
                  gap: 2
                }}>
                    
                </Box>
              )}
            </>
          )}

          {isLoggedIn ? (
            roleMenu
          ) : (
            <>
              {/* <Button
                color="inherit"
                component={Link}
                to="/login"
                sx={{
                  textTransform: 'none',
                  mr: 1,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                Login
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                component={Link}
                to="/signup"
                sx={{
                  textTransform: 'none',
                  borderColor: '#333333',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                Sign Up
              </Button> */}
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;